import { FilterAttributes } from '@crystal-eyes/hooks/profiles/useEnrichedProfiles'
import {
  cosFromDegrees,
  precisionRound,
  sinFromDegrees,
  radiansToDegrees,
} from '@dashboard/lib/math'

import { ColorIconName, Profile, DiscType } from '@dashboard/lib/types'

const DISC_TYPE_ARC = 22.5

interface CircumplexVector {
  degrees: number
  intensity: number
}

export interface CircumplexVectorWithId extends CircumplexVector {
  id: string
}

function vectorToCoordinates(vector: CircumplexVector) {
  return {
    x: cosFromDegrees(vector.degrees) * vector.intensity,
    y: sinFromDegrees(vector.degrees) * vector.intensity,
  }
}

export function translateProgressToDisc(number: number): string | null {
  const intervals: [number, number, string][] = [
    [0, 8, 'C'],
    [8, 17, 'Cs'],
    [17, 25, 'SC'],
    [25, 33, 'Sc'],
    [33, 42, 'S'],
    [42, 50, 'Si'],
    [50, 58, 'IS'],
    [58, 67, 'Is'],
    [67, 75, 'I'],
    [75, 83, 'Id'],
    [83, 92, 'DI'],
    [92, 100, 'Di'],
  ]

  for (const interval of intervals) {
    if (number >= interval[0] && number < interval[1]) {
      return interval[2]
    }
  }

  return null
}

export function DistanceBetweenCircumplexVectors(
  vecOne: CircumplexVector,
  vecTwo: CircumplexVector,
) {
  const [pointOne, pointTwo] = [vecOne, vecTwo].map(vectorToCoordinates)
  const x = Math.abs(pointOne.x - pointTwo.x)
  const y = Math.abs(pointOne.y - pointTwo.y)

  return Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2))
}

export function toDiscType(degrees: number): DiscType {
  const discTypes: DiscType[] = [
    'Is',
    'I',
    'Id',
    'DI',
    'Di',
    'D',
    'Dc',
    'CD',
    'Cd',
    'C',
    'Cs',
    'SC',
    'Sc',
    'S',
    'Si',
  ]

  let startingDegrees = 11.25

  const discType = discTypes.find((type, i) => {
    const lowEnd = startingDegrees
    const highEnd = startingDegrees + DISC_TYPE_ARC
    if (degrees > lowEnd && degrees <= highEnd) return discTypes[i]
    else startingDegrees = highEnd
  })

  return discType || 'IS'
}

export function getDiscTypes(): Record<string, string> {
  return {
    IS: 'Harmonizer',
    Is: 'Encourager',
    I: 'Motivator',
    Id: 'Influencer',
    DI: 'Initiator',
    Di: 'Driver',
    D: 'Captain',
    Dc: 'Architect',
    CD: 'Questioner',
    Cd: 'Skeptic',
    C: 'Analyst',
    Cs: 'Editor',
    SC: 'Stabilizer',
    Sc: 'Planner',
    S: 'Supporter',
    Si: 'Counselor',
  }
}

export function orderedDiscTypes(): Map<DiscType, string> {
  const map = new Map()
  map.set('DI', 'Initiator')
  map.set('Id', 'Influencer')
  map.set('I', 'Motivator')
  map.set('Is', 'Encourager')
  map.set('IS', 'Harmonizer')
  map.set('Si', 'Counselor')
  map.set('S', 'Supporter')
  map.set('Sc', 'Planner')
  map.set('SC', 'Stabilizer')
  map.set('Cs', 'Editor')
  map.set('C', 'Analyst')
  map.set('Cd', 'Skeptic')
  map.set('CD', 'Questioner')
  map.set('Dc', 'Architect')
  map.set('D', 'Captain')
  map.set('Di', 'Driver')

  return map
}

export function toArchetype(discType: string) {
  return getDiscTypes()[discType] || 'N/A'
}

export function circumplexPoint(degrees: number, intensity: number) {
  const length = intensity / 2
  const centerX = 50
  const centerY = 50

  const calculatedX = cosFromDegrees(degrees) * length
  const calculatedY = sinFromDegrees(degrees) * length

  return [
    precisionRound(calculatedX + centerX, 2),
    precisionRound(calculatedY + centerY, 2),
  ]
}

export function getAverageVectorDegreeAndIntensity(
  vectors: CircumplexVectorWithId[],
): { degrees: number; intensity: number } {
  const combinedVectors = { x: 0, y: 0 }
  vectors.forEach(vector => {
    const coordinate = vectorToCoordinates(vector)
    combinedVectors.x += coordinate.x
    combinedVectors.y += coordinate.y
  })
  const x = (combinedVectors.x /= vectors.length)
  const y = (combinedVectors.y /= vectors.length)
  const offset = x < 0 ? 540 : 360
  const degreesFromPosXAxis =
    (radiansToDegrees(Math.atan(y / x)) + offset) % 360
  const averageVector = {
    degrees: degreesFromPosXAxis,
    intensity: Math.sqrt(Math.pow(x, 2) + Math.pow(y, 2)),
  }
  return averageVector
}

const isOverlapping = (
  subject: CircumplexVector,
  comparedVector: CircumplexVector,
  bubbleSize: number,
): boolean => {
  const isOverlapping =
    DistanceBetweenCircumplexVectors(subject, comparedVector) < bubbleSize
  return isOverlapping
}

const createClusters = (
  vectors: CircumplexVectorWithId[][],
  bubbleSize = 36,
  subjectIndex = 0,
): CircumplexVectorWithId[][] => {
  if (vectors.length === 0) return []
  const subject = getAverageVectorDegreeAndIntensity(vectors[subjectIndex])
  const modifiedVectors = [...vectors]

  for (let i = 0; i < vectors.length; i++) {
    if (i === subjectIndex) continue

    if (
      isOverlapping(
        subject,
        getAverageVectorDegreeAndIntensity(vectors[i]),
        bubbleSize * 0.5,
      )
    ) {
      const removed = modifiedVectors.splice(i, 1)[0]
      modifiedVectors[subjectIndex].push(...removed)
      return createClusters(modifiedVectors, bubbleSize, 0)
    }
  }

  if (subjectIndex < modifiedVectors.length - 1) {
    return createClusters(modifiedVectors, bubbleSize, subjectIndex + 1)
  }

  return modifiedVectors
}

export function getCircumplexScores(
  profiles: Profile[],
): CircumplexVectorWithId[] {
  return profiles
    .map(profile => {
      if (!profile.personality) return null

      const { degrees, intensity } = profile.personality

      return {
        id: profile.id,
        degrees,
        intensity,
      }
    })
    .filter((score): score is CircumplexVectorWithId => score !== null)
}

function handleClusters(
  scores: CircumplexVectorWithId[],
  overlapping: CircumplexVectorWithId[][],
): CircumplexVectorWithId[][] {
  const filteredScores = scores
    .filter(s => {
      return !overlapping.flat().some(values => values.id === s.id)
    })
    .map(score => [score])

  return [...filteredScores, ...overlapping]
}

export function distributeCircumplexes(
  scores: CircumplexVectorWithId[],
  bubbleSize: number,
): CircumplexVectorWithId[][] {
  const minScore = 30
  const adjustedScores = scores.map(score => ({
    ...score,
    intensity: minScore + (100 - minScore) * (score.intensity / 100),
  }))

  const formattedScores = adjustedScores.map(score => [score])
  const clusters = createClusters(formattedScores, bubbleSize)
  if (clusters.length) return handleClusters(adjustedScores, clusters)

  return adjustedScores.map(score => [score])
}

export function GetOrderedList(): DiscType[] {
  return [
    'D',
    'Di',
    'DI',
    'Id',
    'I',
    'Is',
    'IS',
    'Si',
    'S',
    'Sc',
    'SC',
    'Cs',
    'C',
    'Cd',
    'CD',
    'Dc',
  ]
}

export function typeDescription(discType: DiscType) {
  switch (discType) {
    case 'D':
      return 'People with the <D (Captain) personality type> tend to be assertive, intense, and ambitious. They are usually pragmatic, results-oriented executors who work quickly and make decisions with firmness and objectivity. With a position on the top left of the DISC, Captains prefer more independence and may be drained when others expect them to be more collaborative. They also may prefer to be in control over a situation, rather than in position to react to others.'
    case 'Di':
      return 'People with the <Di (Driver) personality type> are typically assertive, capable of putting themselves forward boldly, and resistant to influence from others. Convincing others to work toward their goals, they may be seen as decisive, forceful, and persuasive.'
    case 'DI':
      return 'People with the <DI (Initiator) personality type> tend to approach people and situations in an energetic, lively manner. They are likely to enjoy the challenge of meeting new people and winning them over with strong social skills and a knack for being persuasive.'
    case 'Id':
      return 'People with the <Id (Influencer) personality type> tend to be energetic and adventurous, communicating with casual language, bold statements, and a focus on the big picture. They are likely to have an easy, relaxed, casual manner when speaking or interacting with others and enjoy the challenge of meeting new people.'
    case 'I':
      return 'People with the <I (Motivator) personality type> tend to be enthusiastic, cheerful, and outgoing. They typically have an easy, relaxed, casual manner when speaking or interacting with others.'
    case 'Is':
      return 'People with the <Is (Encourager) personality type> tend to be warm, cheerful and light-hearted. Since they tend to be positive and joyful, Encouragers are likely to find a great deal to appreciate in others.'
    case 'IS':
      return 'People with the <IS (Harmonizer) personality type> tend to be warm, even-tempered, accepting. Since they tend to be positive and joyful, Harmonizers are likely to seek lots of social interaction and affirmation.'
    case 'Si':
      return 'People with the <Si personality type> tend to be even-tempered and accepting. Empathetic and supportive, they may seek to help others frequently and sincerely.'
    case 'S':
      return 'People with the <S (Supporter) personality type> tend to be calm, patient and respectful in their interactions with others. Rarely angered or excited, they are likely to work to maintain a peaceful and harmonious environment.'
    case 'Sc':
      return 'People with the <Sc (Planner) personality type> tend to be easy-going and fairly even-keeled in their temperament. They often provide predictability and consistency in their interactions and seek the same thing in return.'
    case 'SC':
      return 'People with the <SC (Stabilizer) personality type> tend to be even-tempered, fairly reserved, and cautious when making decisions, Stabilizers typically offer others predictability and consistency in their interactions and are likely to desire the same response from others.'
    case 'Cs':
      return 'People with the <Cs personality type> tend to be more reserved and solitary. Editors typically limit their contact with others to interactions that are more structured than informal, so they may find themselves hesitant to join in casual group conversations.'
    case 'C':
      return 'People with the <C personality type> tend to be objective, skeptical, and logical in their behavior. They are usually fiercely pragmatic and frequently solve problems with an analytical, fact-driven approach. They are likely to more reserved in groups and may take a long time before they build enough trust to open up.'
    case 'Cd':
      return 'People with the <Cd personality type> tend to be more serious in their demeanor and very logical in their thinking.  They tend to be most comfortable with thoughtful, independent work, and may relate to others in a more distant and detached manner.'
    case 'CD':
      return 'People with the <CD personality type> tend to remove emotions from decision-making as much as possible, valuing efficiency and logic over intuition or social proof. They are usually more reserved in their interactions and relating to others in a more distant and detached manner, building trust slowly.'
    case 'Dc':
      return 'People with the <Dc personality type> tend to be more intense and use a more forceful approach to life than most people. Strong-willed and independent, they typically prefer pursuing your own path and directing their own activities rather than collaborating extensively with others.'
    default:
      return ''
  }
}

export function typeTraits(discType: DiscType): string[] {
  switch (discType) {
    case 'D':
      return ['Assertive', 'Confident', 'Hard-working']
    case 'Di':
      return ['Direct', 'Persuasive', 'Decisive']
    case 'DI':
      return ['Spontaneous', 'Opinionated', 'Ambitious']
    case 'Id':
      return ['Adventurous', 'Visionary', 'Outgoing']
    case 'I':
      return ['Enthusiastic', 'Casual', 'Talkative']
    case 'Is':
      return ['Optimistic', 'Open-minded', 'Inclusive']
    case 'IS':
      return ['Warm', 'Cooperative', 'Creative']
    case 'Si':
      return ['Accommodating', 'Diplomatic', 'Reassuring']
    case 'S':
      return ['Steady', 'Accepting', 'Agreeable']
    case 'Sc':
      return ['Patient', 'Thoughtful', 'Soft-spoken']
    case 'SC':
      return ['Perceptive', 'Consistent', 'Organized']
    case 'Cs':
      return ['Meticulous', 'Calm', 'Purposeful']
    case 'C':
      return ['Objective', 'Collected', 'Reserved']
    case 'Cd':
      return ['Skeptical', 'Pragmatic', 'Accurate']
    case 'CD':
      return ['Persistent', 'Methodical', 'Independent']
    case 'Dc':
      return ['Competitive', 'Vigorous', 'Efficient']
    default:
      return []
  }
}

type DiscTypeWithTraits = {
  discType: DiscType
  traits: string[]
}

export const discTypesWithTraits: DiscTypeWithTraits[] = [
  { discType: 'D', traits: ['Assertive', 'Confident', 'Hard-working'] },
  { discType: 'Di', traits: ['Direct', 'Persuasive', 'Decisive'] },
  { discType: 'DI', traits: ['Spontaneous', 'Opinionated', 'Ambitious'] },
  { discType: 'Id', traits: ['Adventurous', 'Visionary', 'Outgoing'] },
  { discType: 'I', traits: ['Enthusiastic', 'Casual', 'Talkative'] },
  { discType: 'Is', traits: ['Optimistic', 'Open-minded', 'Inclusive'] },
  { discType: 'IS', traits: ['Warm', 'Cooperative', 'Creative'] },
  { discType: 'Si', traits: ['Accommodating', 'Diplomatic', 'Reassuring'] },
  { discType: 'S', traits: ['Steady', 'Accepting', 'Agreeable'] },
  { discType: 'Sc', traits: ['Patient', 'Thoughtful', 'Soft-spoken'] },
  { discType: 'SC', traits: ['Perceptive', 'Consistent', 'Organized'] },
  { discType: 'Cs', traits: ['Meticulous', 'Calm', 'Purposeful'] },
  { discType: 'C', traits: ['Objective', 'Collected', 'Reserved'] },
  { discType: 'Cd', traits: ['Skeptical', 'Pragmatic', 'Accurate'] },
  { discType: 'CD', traits: ['Persistent', 'Methodical', 'Independent'] },
  { discType: 'Dc', traits: ['Competitive', 'Vigorous', 'Efficient'] },
]

interface FilterType {
  discType?: DiscType
  checkBoxValue: string
  checkBoxName: string
  getIsChecked: (item: FilterAttributes) => boolean
  action: { type?: string; value: string; title?: string }
  label: string
}

export interface ProfileTableCheckboxes {
  [key: string]: (data?: any) => FilterType[]
}

export const traitList: string[] = [
  'Accepting',
  'Accommodating',
  'Accurate',
  'Adventurous',
  'Agreeable',
  'Ambitious',
  'Assertive',
  'Calm',
  'Casual',
  'Collected',
  'Competitive',
  'Confident',
  'Consistent',
  'Cooperative',
  'Decisive',
  'Direct',
  'Diplomatic',
  'Efficient',
  'Enthusiastic',
  'Hard-working',
  'Independent',
  'Inclusive',
  'Meticulous',
  'Methodical',
  'Objective',
  'Open-minded',
  'Opinionated',
  'Optimistic',
  'Organized',
  'Outgoing',
  'Patient',
  'Perceptive',
  'Persuasive',
  'Pragmatic',
  'Purposeful',
  'Reassuring',
  'Reserved',
  'Skeptical',
  'Soft-spoken',
  'Spontaneous',
  'Steady',
  'Talkative',
  'Thoughtful',
  'Vigorous',
  'Visionary',
  'Warm',
]

export function personalityIconSrc(personality: string) {
  switch (personality) {
    case 'D':
      return '/static/share/images/D.svg'
    case 'Di':
      return '/static/share/images/Di.svg'
    case 'DI':
      return '/static/share/images/D_I.svg'
    case 'Id':
      return '/static/share/images/Id.svg'
    case 'I':
      return '/static/share/images/I.svg'
    case 'Is':
      return '/static/share/images/Is.svg'
    case 'IS':
      return '/static/share/images/I_S.svg'
    case 'Si':
      return '/static/share/images/Si.svg'
    case 'S':
      return '/static/share/images/S.svg'
    case 'Sc':
      return '/static/share/images/Sc.svg'
    case 'SC':
      return '/static/share/images/S_C.svg'
    case 'Cs':
      return '/static/share/images/Cs.svg'
    case 'C':
      return '/static/share/images/C.svg'
    case 'Cd':
      return '/static/share/images/Cd.svg'
    case 'CD':
      return '/static/share/images/C_D.svg'
    case 'Dc':
      return '/static/share/images/Dc.svg'
    case '1':
      return '/static/share/images/enneagram_icon_1.svg'
    case '2':
      return '/static/share/images/enneagram_icon_2.svg'
    case '3':
      return '/static/share/images/enneagram_icon_3.svg'
    case '4':
      return '/static/share/images/enneagram_icon_4.svg'
    case '5':
      return '/static/share/images/enneagram_icon_5.svg'
    case '6':
      return '/static/share/images/enneagram_icon_6.svg'
    case '7':
      return '/static/share/images/enneagram_icon_7.svg'
    case '8':
      return '/static/share/images/enneagram_icon_8.svg'
    case '9':
      return '/static/share/images/enneagram_icon_9.svg'
    case 'ENFP':
      return '/static/share/images/ENFP.svg'
    case 'ESFJ':
      return '/static/share/images/ESFJ.svg'
    case 'ESFP':
      return '/static/share/images/ESFP.svg'
    case 'INFP':
      return '/static/share/images/INFP.svg'
    case 'INFJ':
      return '/static/share/images/INFJ.svg'
    case 'ISFP':
      return '/static/share/images/ISFP.svg'
    case 'ISFJ':
      return '/static/share/images/ISFJ.svg'
    case 'INTP':
      return '/static/share/images/INTP.svg'
    case 'INTJ':
      return '/static/share/images/INTJ.svg'
    case 'ISTP':
      return '/static/share/images/ISTP.svg'
    case 'ISTJ':
      return '/static/share/images/ISTJ.svg'
    case 'ENTP':
      return '/static/share/images/ENTP.svg'
    case 'ENTJ':
      return '/static/share/images/ENTJ.svg'
    case 'ESTP':
      return '/static/share/images/ESTP.svg'
    case 'ESTJ':
      return '/static/share/images/ESTJ.svg'
    case 'ENFJ':
      return '/static/share/images/ENFJ.svg'
  }
}

export const roleFitScore = (degreesOne: number, degreesTwo: number) => {
  let distance = Math.abs(degreesOne - degreesTwo)
  distance = distance > 180 ? 360 - distance : distance

  return Math.round((1 - distance / 180) * 100)
}

export function getDiscTypeIcon(discType: DiscType): ColorIconName {
  switch (discType) {
    case 'IS':
      return 'apologize'
    case 'Is':
      return 'large-sun'
    case 'I':
      return 'megaphone'
    case 'Id':
      return 'playbook-communicate'
    case 'DI':
      return 'playbook-ribbon'
    case 'Di':
      return 'large-cup'
    case 'D':
      return 'medal'
    case 'Dc':
      return 'paper-with-ruler'
    case 'CD':
      return 'shield'
    case 'Cd':
      return 'rook'
    case 'C':
      return 'playbook-scale'
    case 'Cs':
      return 'bar-chart-with-line-chart'
    case 'SC':
      return 'chart-with-lines'
    case 'Sc':
      return 'playbook-checklist'
    case 'S':
      return 'playbook-hand-heart'
    case 'Si':
      return 'heart-with-outline'
  }
}

export const getDiscTypeQuickTip = (discType: DiscType): string => {
  switch (discType) {
    case 'Dc':
      return 'Be concise'
    case 'D':
      return 'Be direct'
    case 'Di':
      return 'Be brief'
    case 'DI':
      return 'Be casual'
    case 'Id':
      return 'Be compelling'
    case 'I':
      return 'Be engaging'
    case 'Is':
      return 'Be expressive'
    case 'IS':
      return 'Be friendly'
    case 'Si':
      return 'Be relational'
    case 'S':
      return 'Be warm'
    case 'Sc':
      return 'Be polite'
    case 'SC':
      return 'Be formal'
    case 'Cs':
      return 'Be precise'
    case 'C':
      return 'Be detailed'
    case 'Cd':
      return 'Be accurate'
    case 'CD':
      return 'Be objective'
    default:
      return 'Be compelling'
  }
}

export function comparisonWords() {
  return [
    {
      left: { word: 'Risk tolerant', deg: 90 },
      right: { deg: 270, word: 'Risk averse' },
    },
    {
      left: { word: 'Trusting', deg: 0 },
      right: { deg: 180, word: 'Skeptical' },
    },
    {
      left: { word: 'Optimistic', deg: 45 },
      right: { deg: 225, word: 'Pragmatic' },
    },
    {
      left: { word: 'Deliberate', deg: 270 },
      right: { deg: 90, word: 'Fast-paced' },
    },
    {
      left: { word: 'Matter-of-fact', deg: 225 },
      right: { deg: 45, word: 'Expressive' },
    },
    {
      left: { word: 'Autonomous', deg: 180 },
      right: { deg: 0, word: 'Collaborative' },
    },
    {
      left: { word: 'Supporting', deg: 315 },
      right: { deg: 135, word: 'Dominant' },
    },
  ]
}
